'use client';

import DOMPurify from 'isomorphic-dompurify';
import he from 'he';
import parse from 'html-react-parser';
import { useMemo, useSyncExternalStore } from 'react';
import { useGreenhouse } from '@/contexts/GreenhouseProvider';
import { getJobTeam } from '@/libs/greenhouse/greenhouse-functions';
import ShareLinks from '@/components/shared/ShareLinks/ShareLinks';
import Button from '@/components/shared/Buttons/BaseButton';
import SaveJobButton from '@/components/shared/SaveJobButton/SaveJobButton';
import { AnalyticsModuleTrackingProvider } from 'components/analytics/AnalyticsModuleTracking';
import { getColorway } from 'utils/styles';
import { Colorways, LinkTargets } from 'types/constants';

interface CareerDetailContentProps {
  slug: string;
  jobId: number;
  color: Colorways;
}

const subscribe = () => () => {};
const getSnapshot = () => true;
const getServerSnapshot = () => false;

const CareerDetailJobContent = ({ slug, jobId, color }: CareerDetailContentProps) => {
  const greenhouse = useGreenhouse();
  const getJob = greenhouse?.getJob || (() => null);
  const job = getJob(Number(jobId));

  const isMounted = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const jobContent = useMemo(() => {
    if (!job?.content || !isMounted) return null;

    try {
      const decoded = he.decode(job.content);
      const cleanHtml = DOMPurify.sanitize(decoded, {
        USE_PROFILES: { html: true },
      });
      return parse(cleanHtml);
    } catch (e) {
      console.error('Error parsing job content:', e);
      return null;
    }
  }, [job, isMounted]);

  if (!job) return null;

  const team = getJobTeam(job);

  return (
    <>
      <div className={`${getColorway(color)}`}>
        <div className="content-wrapper py-8 sm:pt-16 sm:pb-10">
          <div className="sm:flex sm:gap-8 font-semibold">
            {team && <p>{team}</p>}
            <p>{job?.location?.name}</p>
          </div>
          <h1 className="text-heading-lg-fixed mt-4 sm:mt-2">{job?.title}</h1>
        </div>
      </div>
      <div className="content-wrapper flex flex-col md:flex-row md:gap-18 py-12 md:py-20">
        <div className="col-span-2 w-full md:w-[62%] md:pl-6">
          <div className="block md:hidden mb-12">
            <ShareLinks title={job.title} slug={slug} />
          </div>

          <div
            className="jobCopy 
            [&_>*:first-child]:mt-0
            [&_h2]:font-semibold [&_h2]:text-[2rem] [&_h2]:leading-none [&_h2]:mt-[3rem] [&_h2]:mb-[1.5rem]
            [&_h3]:font-semibold [&_h3]:text-[2rem] [&_h3]:leading-none [&_h3]:mt-[3rem] [&_h3]:mb-[1.5rem]
            [&_p]:font-serif [&_p]:text-base [&_p]:leading-[1.4] [&_p]:mb-[1.5rem]
            [&_p_strong]:font-semibold
            [&_ul]:mb-[1.5rem] [&_ul]:pl-4
            [&_li]:font-serif [&_li]:text-base [&_li]:leading-[1.4] [&_li]:list-disc
            [&_li_strong]:font-semibold">
            {isMounted ? jobContent : <div className="animate-pulse h-40 bg-gray-200 rounded" />}
          </div>
        </div>

        {job.absolute_url && (
          <aside className="white-colorway sticky bottom-0 md:top-24 md:self-start space-y-8 w-full md:w-[38%]">
            <div className="hidden md:block">
              <ShareLinks title={job.title} slug={slug} />
            </div>
            <div className="flex justify-between gap-6 p-2 md:p-6 rounded-md bg-bg-raised-secondary">
              <AnalyticsModuleTrackingProvider>
                <Button
                  href={job.absolute_url}
                  target={LinkTargets.NewTab}
                  variant="solid"
                  className="w-fit"
                  testId="careerDetailButton">
                  Apply Now
                </Button>
                <SaveJobButton job={job} />
              </AnalyticsModuleTrackingProvider>
            </div>
          </aside>
        )}
      </div>
    </>
  );
};

// 4. Explicit Display Name: Fixes ESLint warning
CareerDetailJobContent.displayName = 'CareerDetailJobContent';

export default CareerDetailJobContent;
